'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, RadioGroup, RadioGroupItem } from '@akademi/ui';
import { BlockIcon } from '@akademi/site-builder-runtime';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { mpSurface, isCartTotalZero, isCartEmpty } from './_shared';
import { tx } from '../_shared/coerce';
import { useMarketplaceCart } from './_useMarketplaceCart';

type Localized = string | Record<string, string>;

export interface MarketplacePaymentMethodOption {
  value: string;
  label?: Localized;
}

export interface MarketplacePaymentMethodProps {
  title?: Localized;
  icon?: string;
  /** Show this block even when the live cart's total is already zero — a
   *  free/fully-discounted cart has nothing to pay for. Ignored with no
   *  source mounted. */
  showWhenFree?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_METHODS: MarketplacePaymentMethodOption[] = [
  { value: 'stripe' },
  { value: 'bank_transfer' },
];

/**
 * Payment-method radio card — akademi-lms-v2's cart checkout gateway
 * choice. With a {@link useMarketplaceCart} source mounted the options are
 * the live cart's `paymentGateways` (exactly what the source app renders);
 * with no source it's a demo default. Selection is local UI state — the
 * actual redirect is {@link MarketplaceCheckoutCta}'s `gateway`.
 */
export function MarketplacePaymentMethod({
  title,
  icon = 'credit-card',
  showWhenFree = false,
  paddingOuter,
  paddingInner,
}: MarketplacePaymentMethodProps) {
  const { t } = useTranslation('site-builder-blocks');
  const cart = useMarketplaceCart();

  const list: MarketplacePaymentMethodOption[] = useMemo(() => {
    const gateways = cart?.cart?.paymentGateways;
    if (gateways && gateways.length > 0) return gateways.map((value) => ({ value }));
    return DEFAULT_METHODS;
  }, [cart?.cart?.paymentGateways]);

  const hasMultipleCards = list.filter((m) => m.value !== 'bank_transfer').length > 1;
  const [localValue, setLocalValue] = useState<string | undefined>(undefined);
  const value = cart ? (cart.selectedGateway ?? undefined) : localValue;
  const setValue = cart ? (v: string) => cart.setSelectedGateway(v) : setLocalValue;

  // Auto-pick the only option so the CTA has a gateway to request without
  // requiring an extra click when there's nothing to actually choose between.
  useEffect(() => {
    if (cart && !cart.selectedGateway && list.length === 1 && list[0]) {
      cart.setSelectedGateway(list[0].value);
    }
  }, [cart, list]);

  if (cart && isCartEmpty(cart.cart)) return null;
  if (!showWhenFree && cart && isCartTotalZero(cart.cart)) return null;

  function labelFor(opt: MarketplacePaymentMethodOption): string {
    if (opt.label) return tx(opt.label);
    if (opt.value === 'bank_transfer') return t('marketplace.paymentMethod.bankTransfer');
    const card = t('marketplace.paymentMethod.creditCard');
    if (hasMultipleCards && opt.value === 'stripe') return `${card} — Stripe`;
    if (hasMultipleCards && opt.value === 'paytr') return `${card} — PayTR`;
    if (opt.value === 'stripe' || opt.value === 'paytr' || opt.value === 'credit_card') return card;
    return opt.value;
  }

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardHeader className="p-0">
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
            <BlockIcon value={icon} size={18} />
            {tx(title) || t('marketplace.paymentMethod.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-3">
          <RadioGroup value={value} onValueChange={setValue}>
            {list.map((opt) => {
              const id = `mp-payment-${opt.value}`;
              return (
                <label
                  key={opt.value}
                  htmlFor={id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2, 8px)',
                    cursor: 'pointer',
                  }}
                >
                  <RadioGroupItem id={id} value={opt.value} />
                  <span>{labelFor(opt)}</span>
                </label>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>
    </SectionShell>
  );
}
